/**
 * Repository Factories
 *
 * Provides repository instantiation for Supabase implementations.
 *
 * Usage:
 *   const tradeRepo = getTradeRepository();
 *   const trades = await tradeRepo.getAll(userId);
 *
 * @module repositories/factories
 */
import type { ITradeRepository } from './interfaces/ITradeRepository';
import type { IUserProfileRepository } from './interfaces/IUserProfileRepository';
import type { IPreferencesRepository } from './interfaces/IPreferencesRepository';
import type { IEducationRepository } from './interfaces/IEducationRepository';
import type { IQuizRepository } from './interfaces/IQuizRepository';
import type { IGamificationRepository } from './interfaces/IGamificationRepository';
import type { IForumPostRepository } from './interfaces/IForumPostRepository';
import type { IForumCommentRepository } from './interfaces/IForumCommentRepository';
import type { IForumVoteRepository } from './interfaces/IForumVoteRepository';
import type { SupabaseClient } from '@supabase/supabase-js';

// Supabase implementations
import {
  SupabaseTradeRepository,
  SupabaseUserProfileRepository,
  SupabasePreferencesRepository,
  SupabaseEducationRepository,
  SupabaseQuizRepository,
  SupabaseGamificationRepository,
} from './supabase';
import { SupabaseForumPostRepository } from './supabase/SupabaseForumPostRepository';
import { SupabaseForumCommentRepository } from './supabase/SupabaseForumCommentRepository';
import { SupabaseForumVoteRepository } from './supabase/SupabaseForumVoteRepository';

// =============================================================================
// Singleton Instances
// =============================================================================

let supabaseTradeRepo: SupabaseTradeRepository | null = null;
let supabaseUserProfileRepo: SupabaseUserProfileRepository | null = null;
let supabasePreferencesRepo: SupabasePreferencesRepository | null = null;
let supabaseEducationRepo: SupabaseEducationRepository | null = null;
let supabaseQuizRepo: SupabaseQuizRepository | null = null;
let supabaseGamificationRepo: SupabaseGamificationRepository | null = null;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Get the trade repository.
 *
 * @returns Trade repository implementing ITradeRepository
 */
export function getTradeRepository(): ITradeRepository {
  if (!supabaseTradeRepo) {
    supabaseTradeRepo = new SupabaseTradeRepository();
  }
  return supabaseTradeRepo;
}

/**
 * Get the user profile repository.
 *
 * @returns User profile repository implementing IUserProfileRepository
 */
export function getUserProfileRepository(): IUserProfileRepository {
  if (!supabaseUserProfileRepo) {
    supabaseUserProfileRepo = new SupabaseUserProfileRepository();
  }
  return supabaseUserProfileRepo;
}

/**
 * Get the preferences repository.
 *
 * @returns Preferences repository implementing IPreferencesRepository
 */
export function getPreferencesRepository(): IPreferencesRepository {
  if (!supabasePreferencesRepo) {
    supabasePreferencesRepo = new SupabasePreferencesRepository();
  }
  return supabasePreferencesRepo;
}

/**
 * Get the current storage backend for trades.
 *
 * @returns 'supabase'
 */
export function getTradeStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Get the current storage backend for profiles.
 *
 * @returns 'supabase'
 */
export function getProfileStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Get the current storage backend for preferences.
 *
 * @returns 'supabase'
 */
export function getPreferencesStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Get the education repository.
 *
 * @returns Education repository implementing IEducationRepository
 */
export function getEducationRepository(): IEducationRepository {
  if (!supabaseEducationRepo) {
    supabaseEducationRepo = new SupabaseEducationRepository();
  }
  return supabaseEducationRepo;
}

/**
 * Get the quiz repository.
 *
 * @returns Quiz repository implementing IQuizRepository
 */
export function getQuizRepository(): IQuizRepository {
  if (!supabaseQuizRepo) {
    supabaseQuizRepo = new SupabaseQuizRepository();
  }
  return supabaseQuizRepo;
}

/**
 * Get the gamification repository.
 *
 * @returns Gamification repository implementing IGamificationRepository
 */
export function getGamificationRepository(): IGamificationRepository {
  if (!supabaseGamificationRepo) {
    supabaseGamificationRepo = new SupabaseGamificationRepository();
  }
  return supabaseGamificationRepo;
}

/**
 * Get the current storage backend for education.
 *
 * @returns 'supabase'
 */
export function getEducationStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Get the current storage backend for quizzes.
 *
 * @returns 'supabase'
 */
export function getQuizStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Get the current storage backend for gamification.
 *
 * @returns 'supabase'
 */
export function getGamificationStorageBackend(): 'supabase' {
  return 'supabase';
}

/**
 * Create a forum post repository bound to the provided Supabase client.
 */
export function createForumPostRepository(client: SupabaseClient<any>): IForumPostRepository {
  return new SupabaseForumPostRepository(client);
}

/**
 * Create a forum comment repository bound to the provided Supabase client.
 */
export function createForumCommentRepository(client: SupabaseClient<any>): IForumCommentRepository {
  return new SupabaseForumCommentRepository(client);
}

/**
 * Create a forum vote repository bound to the provided Supabase client.
 */
export function createForumVoteRepository(client: SupabaseClient<any>): IForumVoteRepository {
  return new SupabaseForumVoteRepository(client);
}
